/* eslint-disable */
import { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  styled,
  useTheme,
  InputLabel,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GENERATE_API_TOKEN, REVOKE_API_TOKEN, LIST_API_TOKENS } from "../../gql/mutations/pat";
import { defultSubscriptionType } from "../../common/constant";

// ─── Scope definitions ───────────────────────────────────────────────────────

const SCOPE_GROUPS = [
  {
    label: "Content Management",
    scopes: [
      { value: "CONTENT_READ", label: "Read content queries" },
      { value: "CONTENT_WRITE", label: "Write content queries" },
      { value: "BLOG_GENERATE", label: "Generate blogs" },
      {
        value: "BLOG_SCHEDULE",
        label: "Schedule & publish blogs",
        hint: "Schedule, publish, reschedule, cancel posts — WordPress, Shopify, Magento",
      },
    ],
  },
  {
    label: "SEO Tools",
    scopes: [
      { value: "SEO_READ", label: "Read site audits" },
      { value: "SEO_WRITE", label: "Run site audits" },
      { value: "KEYWORD_RESEARCH", label: "Keyword research" },
      { value: "KEYWORD_RANKING", label: "Keyword ranking" },
    ],
  },
  {
    label: "AI Features",
    scopes: [
      { value: "AI_CHAT", label: "AI Chat" },
      { value: "AI_RECOMMENDATIONS", label: "AI Recommendations" },
      { value: "AI_AGENT", label: "AI Autopilot Agent" },
    ],
  },
  {
    label: "Projects & Monitoring",
    scopes: [
      { value: "PROJECT_READ", label: "Read projects" },
      { value: "PROJECT_WRITE", label: "Manage projects" },
      { value: "UPTIME_READ", label: "Uptime monitoring" },
      { value: "ANALYTICS_READ", label: "Analytics" },
    ],
  },
];

const ALL_SCOPES = SCOPE_GROUPS.flatMap(g => g.scopes.map(s => s.value));

// ─── Styled wrapper ───────────────────────────────────────────────────────────

const BoxWrapper = styled(Box)(({ theme }) => ({
  padding: "16px",
  "& .SectionHeading": {
    fontSize: "20px",
    fontWeight: 700,
    color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
    marginBottom: "8px",
  },
  "& .SectionSubHeading": {
    fontSize: "14px",
    fontWeight: 400,
    color: theme.palette.mode === "dark" ? "#aaaaaa" : "#5A6A85",
    marginBottom: "24px",
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function ScopeChips({ scopes }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {scopes.map(s => (
        <Chip
          key={s}
          label={s.replace(/_/g, " ")}
          size="small"
          sx={{
            fontSize: "11px",
            height: "20px",
            bgcolor: theme.palette.mode === "dark" ? "#333" : "#EEF2FF",
            color: theme.palette.mode === "dark" ? "#aaa" : "#5A6A85",
          }}
        />
      ))}
    </Box>
  );
}

// ─── Token Reveal Dialog ─────────────────────────────────────────────────────

function TokenRevealDialog({ rawToken, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(rawToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog open={Boolean(rawToken)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Your new API token</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy your token now. It will <strong>not</strong> be shown again after you close this dialog.
        </Alert>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "action.hover",
            borderRadius: 1,
            p: 1.5,
            fontFamily: "monospace",
            fontSize: "13px",
            wordBreak: "break-all",
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1, wordBreak: "break-all" }}>
            {rawToken}
          </Typography>
          <Tooltip title={copied ? "Copied!" : "Copy"}>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Use this token as a Bearer token in the Authorization header of your API requests.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          I have copied it, close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Create Token Modal ───────────────────────────────────────────────────────

function CreateTokenModal({ open, onClose, onCreated }) {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [nameError, setNameError] = useState("");
  const [scopeError, setScopeError] = useState("");

  const [generateToken, { loading }] = useMutation(GENERATE_API_TOKEN, {
    onCompleted: data => {
      onCreated(data.generateApiToken);
      handleClose();
    },
    onError: err => {
      toast.error(err.message || "Failed to generate token");
    },
  });

  const handleClose = () => {
    setName("");
    setSelectedScopes([]);
    setExpiresAt("");
    setNameError("");
    setScopeError("");
    onClose();
  };

  const toggleScope = value => {
    setSelectedScopes(prev => (prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]));
    setScopeError("");
  };

  const selectAll = () => setSelectedScopes([...ALL_SCOPES]);
  const clearAll = () => setSelectedScopes([]);

  const handleSubmit = () => {
    let valid = true;
    if (!name.trim()) {
      setNameError("Token name is required");
      valid = false;
    }
    if (selectedScopes.length === 0) {
      setScopeError("Select at least one scope");
      valid = false;
    }
    if (!valid) return;

    generateToken({
      variables: {
        data: {
          name: name.trim(),
          scopes: selectedScopes,
          expiresAt: expiresAt ? (() => { const d = new Date(expiresAt); d.setHours(23, 59, 59, 999); return d.toISOString(); })() : null,
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Generate new API token</DialogTitle>
      <DialogContent>
        {/* Token name */}
        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ mb: 0.5, fontWeight: 600, fontSize: "14px" }}>
            Token name <span style={{ color: "red" }}>*</span>
          </InputLabel>
          <TextField
            fullWidth
            size="small"
            placeholder='e.g. "Claude Agent", "Weekly Blog Script"'
            value={name}
            onChange={e => {
              setName(e.target.value);
              setNameError("");
            }}
            error={Boolean(nameError)}
            helperText={nameError}
          />
        </Box>

        {/* Expiry */}
        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ mb: 0.5, fontWeight: 600, fontSize: "14px" }}>
            Expiry date <span style={{ color: "#5A6A85", fontWeight: 400 }}>(leave blank = never expires)</span>
          </InputLabel>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            inputProps={{ min: new Date().toISOString().split("T")[0] }}
          />
        </Box>

        {/* Scopes */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <InputLabel sx={{ fontWeight: 600, fontSize: "14px" }}>
              Scopes <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="text" onClick={selectAll} sx={{ fontSize: "12px", minWidth: 0, p: "2px 8px" }}>
                Select all
              </Button>
              <Button size="small" variant="text" color="inherit" onClick={clearAll} sx={{ fontSize: "12px", minWidth: 0, p: "2px 8px" }}>
                Clear
              </Button>
            </Box>
          </Box>
          {scopeError && (
            <Typography color="error" variant="caption" sx={{ display: "block", mb: 1 }}>
              {scopeError}
            </Typography>
          )}
          {SCOPE_GROUPS.map(group => (
            <Box key={group.label} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                }}
              >
                {group.label}
              </Typography>
              <FormGroup>
                {group.scopes.map(scope => (
                  <FormControlLabel
                    key={scope.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedScopes.includes(scope.value)}
                        onChange={() => toggleScope(scope.value)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{scope.label}</Typography>
                        {scope.hint && (
                          <Typography variant="caption" color="text.secondary">
                            {scope.hint}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : null}>
          Generate token
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Revoke confirmation dialog ──────────────────────────────────────────────

function RevokeConfirmDialog({ token, onClose, onConfirm, loading }) {
  return (
    <Dialog open={Boolean(token)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Revoke token?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Are you sure you want to revoke <strong>{token?.name}</strong>? Any tool using this token will immediately stop working. This
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="error" disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : null}>
          Revoke
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export default function ApiTokensTab() {
  const theme = useTheme();
  const { userData } = useSelector(state => state.reducer);
  const isFreemium =
    !userData?.subscriptionCredits?.subscriptionType ||
    userData?.subscriptionCredits?.subscriptionType === defultSubscriptionType;

  const [createOpen, setCreateOpen] = useState(false);
  const [revealToken, setRevealToken] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const { data, loading: listLoading, refetch } = useQuery(LIST_API_TOKENS, { fetchPolicy: "network-only", pollInterval: 30000 });

  const [revokeToken, { loading: revoking }] = useMutation(REVOKE_API_TOKEN, {
    onCompleted: () => {
      toast.success("Token revoked");
      setRevokeTarget(null);
      refetch();
    },
    onError: err => toast.error(err.message || "Failed to revoke token"),
  });

  const handleCreated = useCallback(
    newToken => {
      setRevealToken(newToken.rawToken);
      refetch();
    },
    [refetch]
  );

  const handleRevokeConfirm = () => {
    revokeToken({ variables: { tokenId: revokeTarget.id } });
  };

  const tokens = data?.listApiTokens || [];
  const activeTokens = tokens.filter(t => !t.revokedAt);
  const revokedTokens = tokens.filter(t => t.revokedAt);

  if (isFreemium) {
    return (
      <BoxWrapper>
        <Typography className="SectionHeading">API Tokens</Typography>
        <Alert severity="info" sx={{ maxWidth: 600 }}>
          API tokens are available on the <strong>STANDARD plan and above</strong>. Upgrade your plan to create tokens and connect
          external tools to HotspotSEO.
        </Alert>
      </BoxWrapper>
    );
  }

  return (
    <BoxWrapper>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography className="SectionHeading">API Tokens</Typography>
          <Typography className="SectionSubHeading">
            Create long-lived tokens to access HotspotSEO APIs from external tools, automations, or AI agents. Each token is
            scoped to the operations you select.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh token usage">
            <IconButton onClick={() => refetch()} size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Generate new token
          </Button>
        </Box>
      </Box>

      {listLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : tokens.length === 0 ? (
        <Card
          sx={{
            border: `1px dashed ${theme.palette.divider}`,
            boxShadow: "none",
            py: 5,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No API tokens yet. Click <strong>Generate new token</strong> to create one.
          </Typography>
        </Card>
      ) : (
        <>
          {/* Active tokens */}
          {activeTokens.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                ACTIVE TOKENS ({activeTokens.length})
              </Typography>
              <TableContainer component={Card} sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "12px", color: "text.secondary" } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Scopes</TableCell>
                      <TableCell>Last used</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTokens.map(token => (
                      <TableRow key={token.id} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {token.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              bgcolor: "action.hover",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {token.prefix}…
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <ScopeChips scopes={token.scopes} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(token.lastUsedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={token.expiresAt && new Date(token.expiresAt) < new Date() ? "error" : "text.secondary"}>
                            {formatDate(token.expiresAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(token.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Revoke token">
                            <IconButton size="small" color="error" onClick={() => setRevokeTarget(token)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Revoked tokens */}
          {revokedTokens.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                REVOKED TOKENS ({revokedTokens.length})
              </Typography>
              <TableContainer component={Card} sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: "none", opacity: 0.6 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "12px", color: "text.secondary" } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Revoked on</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revokedTokens.map(token => (
                      <TableRow key={token.id} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                            {token.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {token.prefix}…
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="error">
                            {formatDate(token.revokedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(token.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}

      {/* Dialogs */}
      <CreateTokenModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <TokenRevealDialog rawToken={revealToken} onClose={() => setRevealToken(null)} />
      <RevokeConfirmDialog token={revokeTarget} onClose={() => setRevokeTarget(null)} onConfirm={handleRevokeConfirm} loading={revoking} />
    </BoxWrapper>
  );
}
